/**
 * Truncate an AIS course/speed vector at the coastline. A real vessel won't
 * steam across land, so a predictor line that ploughs over a headland is just
 * noise, and worse it can hide the fact the target must be about to turn. We clip
 * the line where it first crosses a charted land polygon (LNDARE), so it stops at
 * the coast.
 *
 * Pure geometry in lon/lat degrees, so it's easy to test on its own. The caller
 * supplies the candidate land rings (the outer boundaries of nearby land
 * polygons) already pruned to the vector's neighbourhood; we walk the segment out
 * from the target and return where it first meets land.
 */
export type LngLat = [number, number];

interface Hit {
  /** Parameter along from->to, 0 at `from`, 1 at `to`. */
  t: number;
  point: LngLat;
}

/**
 * Where segment from->to first crosses into land, walking out from `from`, or the
 * original `to` if it never does. Returns null when the target itself sits inside
 * land (the whole vector is over land, so there's nothing worth drawing). With no
 * rings supplied nothing is clipped and `to` is returned unchanged.
 */
export function clipVectorToLand(from: LngLat, to: LngLat, landRings: LngLat[][]): LngLat | null {
  if (!landRings.length) return to;
  // A target already on land (bad data, or a vessel alongside on a drying berth):
  // the whole vector is over land, so signal "draw nothing".
  if (pointInAnyRing(from, landRings)) return null;

  // Segment bounding box, so each ring edge can be cheaply rejected before the
  // full intersection test — this keeps a long, detailed coastline polygon from
  // costing us every one of its edges on every target.
  const minX = Math.min(from[0], to[0]);
  const maxX = Math.max(from[0], to[0]);
  const minY = Math.min(from[1], to[1]);
  const maxY = Math.max(from[1], to[1]);

  let best: LngLat | null = null;
  let bestT = Infinity;
  for (const ring of landRings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const c = ring[j];
      const d = ring[i];
      // Cheap reject: edge wholly to one side of the segment's box can't cross it.
      if (Math.max(c[0], d[0]) < minX || Math.min(c[0], d[0]) > maxX) continue;
      if (Math.max(c[1], d[1]) < minY || Math.min(c[1], d[1]) > maxY) continue;
      const hit = segIntersect(from, to, c, d);
      if (hit && hit.t < bestT) {
        bestT = hit.t;
        best = hit.point;
      }
    }
  }
  return best ?? to;
}

/**
 * Intersection of segment a->b with segment c->d, or null if they don't meet.
 * Returns the crossing point and its parameter t along a->b (0 at a, 1 at b).
 */
function segIntersect(a: LngLat, b: LngLat, c: LngLat, d: LngLat): Hit | null {
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  const sx = d[0] - c[0];
  const sy = d[1] - c[1];
  const denom = rx * sy - ry * sx;
  if (denom === 0) return null; // parallel or degenerate
  const qpx = c[0] - a[0];
  const qpy = c[1] - a[1];
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { t, point: [a[0] + t * rx, a[1] + t * ry] };
}

/** True if the point falls inside any of the rings (even-odd ray cast per ring). */
function pointInAnyRing(pt: LngLat, rings: LngLat[][]): boolean {
  for (const ring of rings) if (pointInRing(pt, ring)) return true;
  return false;
}

/** Standard even-odd ray-cast point-in-polygon for a single ring. */
function pointInRing(pt: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  const x = pt[0];
  const y = pt[1];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const hit = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}
